<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import KpiMetricService from '@/services/quality/kpiMetric.service'
import { VDataTableServer } from 'vuetify/lib/labs/VDataTable/index.mjs'

definePage({
  meta: {
    name: 'incident-master-kpi-metric',
    subject: 'quality.menu',
    action: 'master_kpi_metric',
  },
})

const activeType = ref('periodic')

const headers = computed(() => {
  const base = [
    {
      title: 'Title',
      key: 'title',
    },
    {
      title: 'Domain',
      key: 'domain.name',
      sortable: false,
    },
  ]

  if (activeType.value === 'periodic') {
    base.push({
      title: 'Frequency',
      key: 'frequency',
    })
  }

  base.push(
    {
      title: 'Users',
      key: 'users',
      sortable: false,
    },
    {
      title: 'Locations',
      key: 'locations',
      sortable: false,
    },
    {
      title: 'Actions',
      key: 'actions',
      sortable: false,
    },
  )

  return base
})

const filter = computed(() => {
  const base = {}

  if (activeType.value === 'periodic') {
    base.frequency = 'not-null'
  } else {
    base.frequency = 'null'
  }

  return base
})

const searchQuery = ref('')
const itemsPerPage = ref(10)
const pageNumber = ref(1)
const sortBy = ref()
const orderBy = ref()

const {
  isFetching,
  data: kpiMetricsData,
  execute: fetchKpiMetrics,
} = useApi(createUrl('/incident/kpi-metrics', {
  query: {
    paged: 1,
    q: searchQuery,
    itemsPerPage,
    page: pageNumber,
    sort: sortBy,
    order_by: orderBy,
    search: filter,
    with: ['domain', 'users', 'locations'],
  },
}))

const kpiMetrics = computed(() => kpiMetricsData.value?.data ?? kpiMetricsData.value ?? [])
const totalKpiMetrics = computed(() => kpiMetricsData.value?.meta?.total ?? kpiMetricsData.value?.length ?? 0)

// Reset pagination on type change
watch(activeType, () => {
  pageNumber.value = 1
})

// Delete
const isConfirmDialogVisible = ref(false)
const deletingKpiMetric = ref(null)
const refConfirmDialog = ref()

const showConfirmDialog = item => {
  deletingKpiMetric.value = item
  isConfirmDialogVisible.value = true
}

const deleteKpiMetric = async (confirm, data) => {
  if (data?.id && confirm) {
    await KpiMetricService.destroy(
      data.id,
      () => {
        refConfirmDialog.value.onSuccess()
        isConfirmDialogVisible.value = false
        fetchKpiMetrics()
      },
      () => {
        refConfirmDialog.value.onError()
      },
    )
  }
}

watch(isConfirmDialogVisible, value => {
  if (!value) deletingKpiMetric.value = null
})
</script>

<template>
  <VCard
    variant="flat"
    class="border"
  >
    <VCardText class="d-flex flex-wrap py-4 gap-4">
      <div
        class="me-3 d-flex gap-3"
        style="inline-size: 24rem;"
      >
        <VBtnToggle
          v-model="activeType"
          mandatory
          color="primary"
          variant="tonal"
          density="compact"
        >
          <VBtn value="periodic">
            Periodic
          </VBtn>
          <VBtn value="static">
            Static
          </VBtn>
        </VBtnToggle>
      </div>

      <VSpacer />

      <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
        <!-- Search -->
        <div style="inline-size: 12rem;">
          <AppTextField
            v-model="searchQuery"
            placeholder="Search"
            density="compact"
          />
        </div>
        <VBtn
          variant="flat"
          prepend-icon="tabler-plus"
          :to="{ name: 'app-quality-master-kpi-metric-setup' }"
        >
          Create KPI Metrics
        </VBtn>

        <IconBtn
          variant="tonal"
          class="rounded"
          :disabled="isFetching"
          @click="fetchKpiMetrics"
        >
          <VIcon icon="tabler-refresh" />
        </IconBtn>
      </div>
    </VCardText>

    <VDivider />

    <VDataTableServer
      v-model:items-per-page="itemsPerPage"
      v-model:page="pageNumber"
      :items-length="totalKpiMetrics"
      :items="kpiMetrics"
      :headers="headers"
      fixed-header
      class="text-no-wrap"
      :loading="isFetching"
    >
      <template #item.title="{ item }">
        {{ item.title }}
      </template>
      <template #item.domain.name="{ item }">
        <VChip
          v-if="item.domain?.name"
          size="small"
          color="info"
          variant="tonal"
        >
          {{ item.domain.name }}
        </VChip>
        <span
          v-else
          class="text-medium-emphasis"
        >N/A</span>
      </template>
      <template #item.frequency="{ item }">
        <span class="text-capitalize">{{ item.frequency ?? 'N/A' }}</span>
      </template>
      <template #item.users="{ item }">
        <div
          v-if="item.users?.length > 0"
          class="v-avatar-group"
        >
          <VAvatar
            v-for="user in item.users"
            :key="user.id"
            size="32"
            :color="$vuetify.theme.current.dark ? '#4A5072' : '#f6f6f7'"
          >
            <VTooltip
              activator="parent"
              location="top"
            >
              {{ user.name }}
            </VTooltip>
            <VImg
              v-if="user.avatar"
              :src="user.avatar"
            />
            <span v-else>
              {{ avatarText(user.name) }}
            </span>
          </VAvatar>
        </div>
        <span
          v-else
          class="text-medium-emphasis"
        >N/A</span>
      </template>
      <template #item.locations="{ item }">
        <div
          v-if="item.locations?.length > 0"
          class="d-flex flex-wrap gap-1"
        >
          <VChip
            v-for="location in item.locations"
            :key="location.id"
            size="small"
            variant="tonal"
          >
            {{ location.name }}
          </VChip>
        </div>
        <span
          v-else
          class="text-medium-emphasis"
        >N/A</span>
      </template>
      <template #item.actions="{ item }">
        <IconBtn :to="{ name: 'app-quality-master-kpi-metric-setup', params: { id: item.id } }">
          <VIcon icon="tabler-edit" />
        </IconBtn>
        <IconBtn @click="showConfirmDialog(item)">
          <VIcon icon="tabler-trash" />
        </IconBtn>
      </template>
    </VDataTableServer>
  </VCard>

  <ConfirmDialog
    ref="refConfirmDialog"
    v-model:isDialogVisible="isConfirmDialogVisible"
    confirmation-question="Are you sure you want to delete this KPI Metric?"
    confirm-msg="KPI Metric Deleted."
    confirm-title="Deleted!"
    cancel-title="Error!"
    cancel-msg="Something Went Wrong."
    :is-confirm-visible="false"
    :is-error-visible="false"
    :data="deletingKpiMetric"
    @confirm="deleteKpiMetric"
  />
</template>

<style>
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>
